import React, { useMemo, useState, useEffect } from 'react';
import { useAutoaddress } from './hooks/useAutoaddress';
import { useInvoicePaymentConfig } from './hooks/useInvoicePaymentConfig';
import SvgIcon from '../utils/SvgIcon';
import PaymentErrorMessage from './components/PaymentErrorMessage';

interface BillingAddressRequest {
  line1: string;
  line2: string | null;
  line3: string | null;
  city: string;
  postalCode: string;
  country: string;
}

interface InvoicePaymentRequest {
  amount: number;
  invoiceNumber: string;
  invoiceId: string;
  name: string;
  email: string;
  paymentType: 'Invoice';
  address: BillingAddressRequest;
}

function Inner() {
  const {
    addressProfile,
    addressLookupKey,
    amount,
    invoiceId,
    invoiceNumber,
    countryCode,
    email,
    name,
    addressLine1,
    addressLine2,
    addressLine3,
    town,
    city,
    postcode,
    labels,
    successUrl,
    declinedUrl,
    returnUrl,
  } = useInvoicePaymentConfig();

  const [userDetails, setUserDetails] = useState<Record<string, string> | null>({
    email: email,
    fullName: name,
    addressLine1: addressLine1,
    addressLine2: addressLine2,
    addressLine3: addressLine3,
    townCity: town || city,
    postCode: postcode,
  });

  const [isManualEntry, setIsManualEntry] = useState(!!addressLine1 || !!town || !!city || !!postcode);
  const [isError, setIsError] = useState(false);
  const [isAddressTab, setIsAddressTab] = useState(true);
  const [isPaymentTab, setIsPaymentTab] = useState(false);
  const [isPaymentDisabled, setIsPaymentDisabled] = useState(true);
  const [iframeSrc, setIframeSrc] = useState('about:blank');

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (event.data?.type !== 'globalpay:complete') {
        return;
      }

      const { success, invoiceId, invoiceNumber, amount } = event.data;

      const baseUrl = success ? successUrl : declinedUrl;

      const targetUrl = new URL(baseUrl, globalThis.location.origin);

      targetUrl.searchParams.set('invoiceId', invoiceId);

      if (invoiceNumber) {
        targetUrl.searchParams.set('invoiceNumber', invoiceNumber);
      }

      if (typeof amount === 'number') {
        targetUrl.searchParams.set('amount', amount.toString());
      }

      if (!success) {
        targetUrl.searchParams.set('declined', 'true');
      }

      if (returnUrl) {
        targetUrl.searchParams.set('returnUrl', returnUrl);
      }

      window.top?.location.assign(targetUrl.toString());
    }

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [successUrl, declinedUrl, returnUrl]);

  const urlParams = useMemo(() => new URLSearchParams(globalThis.location.search), []);
  const paymentDeclined = urlParams.get('declined') === 'true';
  const enterManuallyText = isManualEntry ? 'Search again' : 'Enter manually';

  useAutoaddress('#searchAddress', setUserDetails, setIsManualEntry, isManualEntry, {
    licenceKey: addressLookupKey,
    vanityMode: true,
    country: countryCode,
    addressProfile: addressProfile,
    placeHolderLabel: 'Type to search address',
  });

  function handleContinueToPayment() {
    if (!isManualEntry) {
      setIsManualEntry(true);

      setTimeout(() => {
        validateAndShowErrors();
      }, 0);
      return;
    }

    validateAndProceed();
  }

  function validateAndShowErrors() {
    const fieldsToValidate = ['email', 'fullName', 'addressLine1', 'townCity', 'postCode'];
    let hasInvalidField = false;

    for (const field of fieldsToValidate) {
      if (!userDetails?.[field] || userDetails?.[field]?.trim() === '') {
        hasInvalidField = true;
        document.getElementById(field)?.classList.add('portal-invoice-payment__input--error');
      } else {
        document.getElementById(field)?.classList.remove('portal-invoice-payment__input--error');
      }
    }

    return hasInvalidField;
  }

  async function fetchPayment() {
    const requestBody: InvoicePaymentRequest = {
      amount: Number.parseFloat(amount),
      invoiceNumber: invoiceNumber,
      invoiceId: invoiceId,
      name: userDetails?.fullName ?? '',
      email: userDetails?.email ?? '',
      paymentType: 'Invoice',
      address: {
        line1: userDetails?.addressLine1 || '',
        line2: userDetails?.addressLine2 || null,
        line3: userDetails?.addressLine3 || null,
        city: userDetails?.townCity || '',
        postalCode: userDetails?.postCode ?? '',
        country: countryCode,
      },
    };

    try {
      const response = await fetch('/api/payment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        const data = await response.json();

        if (!data.url || typeof data.url !== 'string') {
          setIsError(true);
          console.error('Payment API response is missing a valid URL');
          return;
        }

        setIframeSrc(data.url);
        setIsAddressTab(false);
        setIsPaymentTab(true);
        setIsPaymentDisabled(false);
      } else {
        setIsError(true);
        console.error('Payment API request failed with status:', response.status);
      }
    } catch (error) {
      setIsError(true);
      console.error('Error fetching payment API:', error);
    }
  }

  function validateAndProceed() {
    const hasInvalidField = validateAndShowErrors();

    if (hasInvalidField) return;

    fetchPayment();
  }

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { id, value } = event.target as HTMLInputElement;
    setUserDetails(prev => (prev ? { ...prev, [id]: value } : { [id]: value }));
  }

  function handleBlurRemoveError(event: React.FocusEvent<HTMLInputElement, Element>) {
    const target = event.target as HTMLInputElement;
    const inputId = target.id;

    if (userDetails?.[inputId] && userDetails?.[inputId]?.trim() !== '') {
      target.classList.remove('portal-invoice-payment__input--error');
    } else {
      target.classList.add('portal-invoice-payment__input--error');
    }
  }

  function handleAddressTabBtn() {
    setIsAddressTab(true);
    setIsPaymentTab(false);
    setIsPaymentDisabled(true);
  }

  function handleCancel() {
    window.history.back();
  }

  return (
    <div className="portal-invoice-payment__payment-card">
      <div className="portal-invoice-payment__nav-buttons" role="tablist">
        <button
          id="addressTab"
          className="portal-invoice-payment__nav-button"
          aria-selected={isAddressTab}
          aria-controls="addressTabPanel"
          role="tab"
          onClick={handleAddressTabBtn}
        >
          <span className="number">1</span>
          <span>{labels.addressTabHeading}</span>
        </button>
        <button
          id="paymentTab"
          className="portal-invoice-payment__nav-button"
          aria-selected={isPaymentTab}
          aria-controls="paymentTabPanel"
          role="tab"
          disabled={isPaymentDisabled}
          tabIndex={-1}
        >
          <span className="number">2</span>
          <span>{labels.paymentTabHeading}</span>
        </button>
      </div>
      {isAddressTab && (
        <div
          id="addressTabPanel"
          className="portal-invoice-payment__address-tab"
          role="tabpanel"
          aria-labelledby="addressTab"
        >
          {paymentDeclined && (
            <PaymentErrorMessage
              type="declined"
              heading={labels.paymentDeclinedHeading}
              message={labels.paymentDeclinedMessage}
            />
          )}
          <div className="portal-invoice-payment__contact">
            <h2 className="portal-invoice-payment__address-heading">{labels.contactSectionHeading}</h2>
            <div className="portal-invoice-payment__input-wrapper">
              <label className="portal-invoice-payment__label" htmlFor="email">
                {labels.emailLabel}
              </label>
              <input
                className="portal-invoice-payment__input"
                id="email"
                type="email"
                value={userDetails?.email || ''}
                onChange={handleInputChange}
                onBlur={handleBlurRemoveError}
                disabled={true}
              />
            </div>
          </div>
          <div
            id="addressTabPanel"
            className="portal-invoice-payment__address-tab"
            role="tabpanel"
            aria-labelledby="addressTab"
          >
            <h2 className="portal-invoice-payment__address-heading">{labels.addressSectionHeading}</h2>
            <div className="portal-invoice-payment__input-wrapper">
              <label className="portal-invoice-payment__label" htmlFor="fullName">
                {labels.nameLabel}
              </label>
              <input
                className="portal-invoice-payment__input"
                id="fullName"
                type="text"
                value={userDetails?.fullName || ''}
                onChange={handleInputChange}
                placeholder="Enter your name"
                onBlur={handleBlurRemoveError}
              />
            </div>
            <div>
              {!isManualEntry && (
                <div className="portal-invoice-payment__search-container">
                  <label className="portal-invoice-payment__label" htmlFor="searchAddress">
                    Address
                  </label>
                  <div id="searchAddress"></div>
                </div>
              )}
              {isManualEntry && (
                <div className="portal-invoice-payment__address-line-container">
                  <div className="portal-invoice-payment__input-wrapper">
                    <label className="portal-invoice-payment__label" htmlFor="addressLine1">
                      Address line 1
                    </label>
                    <input
                      className="portal-invoice-payment__input"
                      id="addressLine1"
                      type="text"
                      value={userDetails?.addressLine1 || ''}
                      onChange={handleInputChange}
                      onBlur={handleBlurRemoveError}
                    />
                  </div>
                  <div className="portal-invoice-payment__input-wrapper">
                    <label className="portal-invoice-payment__label" htmlFor="addressLine2">
                      Address line 2 (optional)
                    </label>
                    <input
                      className="portal-invoice-payment__input"
                      id="addressLine2"
                      type="text"
                      value={userDetails?.addressLine2 || ''}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="portal-invoice-payment__input-wrapper">
                    <label className="portal-invoice-payment__label" htmlFor="addressLine3">
                      Address line 3 (optional)
                    </label>
                    <input
                      className="portal-invoice-payment__input"
                      id="addressLine3"
                      type="text"
                      value={userDetails?.addressLine3 || ''}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              )}
              <button
                className="portal-invoice-payment__enter-manually"
                onClick={() => {
                  setIsManualEntry(prev => !prev);
                }}
              >
                {enterManuallyText}
              </button>
            </div>
            <div className="portal-invoice-payment__input-wrapper">
              <label className="portal-invoice-payment__label" htmlFor="townCity">
                Town/city
              </label>
              <input
                className="portal-invoice-payment__input"
                id="townCity"
                type="text"
                placeholder="Enter town/city"
                value={userDetails?.townCity || ''}
                onChange={handleInputChange}
                onBlur={handleBlurRemoveError}
              />
            </div>
            <div className="portal-invoice-payment__input-wrapper">
              <label className="portal-invoice-payment__label" htmlFor="postCode">
                Postcode
              </label>
              <input
                className="portal-invoice-payment__input"
                id="postCode"
                type="text"
                placeholder="Enter postcode"
                value={userDetails?.postCode || ''}
                onChange={handleInputChange}
                onBlur={handleBlurRemoveError}
              />
            </div>
            {isError && (
              <PaymentErrorMessage
                type="api-error"
                heading={labels.paymentErrorHeading}
                message={labels.paymentErrorMessage}
              />
            )}
            <div className="portal-invoice-payment__input-wrapper portal-invoice-payment__ctas">
              <button className="portal-invoice-payment__cancel-cta cta cta--secondary" onClick={handleCancel}>
                <span>{labels.cancelButtonLabel}</span>
              </button>
              <button
                className="portal-invoice-payment__continue-cta cta cta--primary"
                onClick={handleContinueToPayment}
              >
                <span>Continue to payment</span>
                <SvgIcon iconName="arrow-right-medium-portal" />
              </button>
            </div>
          </div>
        </div>
      )}
      {isPaymentTab && (
        <div className="portal-invoice-payment__payment-tab">
          <iframe title="Payment iframe" className="portal-invoice-payment__iframe" src={iframeSrc}></iframe>
        </div>
      )}
    </div>
  );
}

export default function PortalInvoicePayment() {
  return <Inner />;
}
